/**
 * handlers.missionDay.ts — offline replay for the MISSION-DAY SESSION writes: the
 * day lifecycle (start / finish / home-return), a walk-in joker stop, and a
 * lead's status change.
 *
 * Charter: ONE bounded context — what a field rep writes WHILE a mission day is
 * running. Registered by the `./handlers` facade.
 */

import { registerQueueHandler } from './offlineQueue'
import { startMissionDay, finishMissionDay, recordHomeReturn } from '../api/missionVisits'
import { updateMissionLead } from '../api/missions'
import type { MissionLead } from '../api/missions'
import { createJokerStopViaSL } from '../api/jokerStops'
import { QUEUE_TYPES } from './queueTypes'

export interface LeadStatusUpdatePayload {
  missionLeadId: string
  patch: Partial<MissionLead>
}

/** Joker-stop create — field-rep write that used to brick offline. */
export interface JokerStopCreatePayload {
  missionId: string
  userEmail: string
  leadId?: string
  companyName: string
  reason: string
  /** Ort (city) of a walk-in — replayed so an offline-queued joker keeps it. */
  city?: string | null
}

/**
 * Mission day-lifecycle write (start / finish / home-return). The SL is the
 * source of truth (POST /v1/missions/{id}/start|finish|home-return); the
 * mission_day_state table is a FE-only mirror with no SL write path, so the
 * lifecycle POST is what we queue + replay when offline.
 */
export interface MissionDayLifecyclePayload {
  missionId: string
}

export function registerMissionDayHandlers(): void {
  registerQueueHandler(QUEUE_TYPES.LEAD_STATUS_UPDATE, async (payload) => {
    const p = payload as LeadStatusUpdatePayload
    await updateMissionLead(p.missionLeadId, p.patch)
  })

  // Legacy queued joker-stop creates (pre-FR-FE-JOKER-AS-VISIT-FLOW-2026-06-10,
  // when the joker was an offline-first direct-Supabase insert into the
  // `joker_stops` table). Those rows replay through the SL route now —
  // `createJokerStop` (direct Supabase) replayed as the field rep would 401
  // against RLS. The rep's WHY (`reason`) + Ort (`city`) replay too so a
  // queued joker keeps them; `userEmail` is unused (the SL derives the actor
  // from the session).
  registerQueueHandler(QUEUE_TYPES.JOKER_STOP_CREATE, async (payload) => {
    const p = payload as JokerStopCreatePayload
    await createJokerStopViaSL({
      missionId: p.missionId,
      companyName: p.companyName,
      leadId: p.leadId !== null && p.leadId !== undefined ? p.leadId : null,
      reason: p.reason,
      city: p.city !== null && p.city !== undefined ? p.city : null,
    })
  })

  registerQueueHandler(QUEUE_TYPES.MISSION_DAY_START, async (payload) => {
    const p = payload as MissionDayLifecyclePayload
    await startMissionDay(p.missionId)
  })

  registerQueueHandler(QUEUE_TYPES.MISSION_DAY_FINISH, async (payload) => {
    const p = payload as MissionDayLifecyclePayload
    await finishMissionDay(p.missionId)
  })

  registerQueueHandler(QUEUE_TYPES.MISSION_DAY_HOME_RETURN, async (payload) => {
    const p = payload as MissionDayLifecyclePayload
    await recordHomeReturn(p.missionId)
  })
}
